import React from "react";
import { LucideIcon, AlertCircle, Check } from "lucide-react";
import { QuestionOption } from "../types";

// Scientific Illustration Placeholder
const QuestionVisual = ({
  questionId,
  Icon,
}: {
  questionId: string;
  Icon: LucideIcon;
}) => {
  return (
    <div className="w-full h-full bg-slate-50 flex items-center justify-center relative overflow-hidden group">
      {/* Grid Background Pattern */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(#cbd5e1 1px, transparent 1px)",
          backgroundSize: "20px 20px",
          opacity: 0.3,
        }}
      ></div>

      {/* Dynamic Visuals based on Question ID */}
      <div className="relative z-10 flex flex-col items-center justify-center transition-transform duration-500 group-hover:scale-105">
        <div className="w-32 h-32 md:w-48 md:h-48 bg-white border border-slate-200 shadow-sm rounded-full flex items-center justify-center mb-4">
          <Icon size={64} className="text-slate-700" strokeWidth={1.5} />
        </div>
        {questionId === "age" && (
          <div className="absolute -right-4 top-10 bg-white px-3 py-1 rounded border border-slate-200 text-xs font-mono text-slate-500 shadow-sm">
            Age Analysis
          </div>
        )}
        {questionId === "concern" && (
          <div className="absolute -left-4 bottom-10 bg-white px-3 py-1 rounded border border-slate-200 text-xs font-mono text-slate-500 shadow-sm">
            Symptom Check
          </div>
        )}
      </div>

      {/* Decorative Technical Lines */}
      <div className="absolute top-4 right-4 w-16 h-16 border-t border-r border-slate-300 opacity-50"></div>
      <div className="absolute bottom-4 left-4 w-16 h-16 border-b border-l border-slate-300 opacity-50"></div>
    </div>
  );
};

interface QuestionProps {
  data: QuestionOption;
  currentStep: number;
  totalSteps: number;
  onAnswer: (option: string) => void;
}

const Question: React.FC<QuestionProps> = ({
  data,
  currentStep,
  totalSteps,
  onAnswer,
}) => (
  <div className="min-h-screen bg-white flex flex-col md:flex-row pt-14">
    {/* Left (Desktop) / Top (Mobile): Visual Area */}
    <div className="w-full md:w-1/2 h-[35vh] md:h-[calc(100vh-3.5rem)] bg-slate-50 relative border-b md:border-b-0 md:border-r border-slate-200 order-1 md:order-2">
      <QuestionVisual questionId={data.id} Icon={data.icon} />

      {/* Mobile Progress Overlay */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-200 md:hidden">
        <div
          className="h-full bg-orange-500 transition-all duration-300"
          style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
        />
      </div>
    </div>

    {/* Right (Desktop) / Bottom (Mobile): Interaction Area */}
    <div className="w-full md:w-1/2 flex flex-col px-6 md:px-16 py-8 md:py-12 overflow-y-auto order-2 md:order-1 h-[calc(65vh-3.5rem)] md:h-[calc(100vh-3.5rem)]">
      {/* Desktop Progress */}
      <div className="hidden md:flex items-center gap-4 mb-12">
        <div className="text-xs font-mono text-slate-400">
          STEP {currentStep + 1}/{totalSteps}
        </div>
        <div className="flex-1 h-px bg-slate-100">
          <div
            className="h-0.5 bg-slate-900 transition-all duration-300"
            style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      <div className="flex-1 flex flex-col justify-center max-w-lg mx-auto w-full">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3 tracking-tight">
          {data.question}
        </h2>
        {data.sub && (
          <p className="text-slate-500 mb-8 flex items-center gap-2 text-sm">
            <AlertCircle size={14} /> {data.sub}
          </p>
        )}

        <div className="space-y-3">
          {data.options.map((option, idx) => (
            <button
              key={idx}
              onClick={() => onAnswer(option)}
              className="group w-full p-4 text-left border border-slate-200 hover:border-slate-900 bg-white transition-all flex items-center justify-between"
            >
              <span className="text-base font-medium text-slate-700 group-hover:text-slate-900">
                {option}
              </span>
              <div className="w-5 h-5 border border-slate-300 rounded-sm flex items-center justify-center group-hover:bg-slate-900 group-hover:border-slate-900 transition-colors">
                <Check
                  size={12}
                  className="text-white opacity-0 group-hover:opacity-100"
                />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default Question;
